import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { loginSchema, type LoginInput, type LoginResponse } from '@bifeai/shared';
import { Button, Callout, Field, Input } from '@bifeai/ui';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { reloadMe } from '../../lib/session';
import { PinSwitchDialog, usePinUsers } from '../../components/PinSwitchDialog';
import { AuthLayout } from './AuthLayout';

export function routeAfterLogin(next: LoginResponse['next'], target: string): string {
  if (next === 'verify_mfa') return '/ingresar/codigo';
  if (next === 'setup_mfa') return '/activar-2fa';
  if (next === 'change_password') return '/cambiar-clave';
  return target;
}

function safeNext(value: string | null): string {
  return value && value.startsWith('/') && !value.startsWith('//') ? value : '/';
}

export function LoginPage() {
  const navigate = useNavigate();
  // En una terminal registrada del mostrador se puede entrar con el PIN, sin email ni contraseña.
  const terminal = usePinUsers();
  const [pinOpen, setPinOpen] = useState(false);
  const qc = useQueryClient();
  const resetAvailable = useQuery({
    queryKey: ['auth', 'reset-available'],
    queryFn: () => api.get<{ available: boolean }>('/auth/password/reset-available'),
    staleTime: 5 * 60_000,
  });
  const [params] = useSearchParams();
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({ resolver: zodResolver(loginSchema) });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      const res = await api.post<LoginResponse>('/auth/login', values);
      await reloadMe(qc);
      navigate(routeAfterLogin(res.next, safeNext(params.get('next'))), {
        replace: true,
      });
    } catch (err) {
      setFormError(errorMessage(err));
    }
  });

  return (
    <AuthLayout title="Ingresá a tu carnicería" subtitle="Usá el email y la contraseña que te dio el dueño o BifeAI.">
      <form className="bf-form" onSubmit={onSubmit} noValidate>
        {formError && <Callout tone="danger">{formError}</Callout>}
        <Field label="Email" error={errors.email?.message}>
          <Input type="email" autoComplete="username" autoFocus placeholder="nombre@carniceria.com" {...register('email')} />
        </Field>
        <Field label="Contraseña" error={errors.password?.message}>
          <Input type="password" autoComplete="current-password" {...register('password')} />
        </Field>
        <Button type="submit" variant="primary" block loading={isSubmitting}>
          Ingresar
        </Button>
        {terminal.data && terminal.data.users.length > 0 && (
          <Button block onClick={() => setPinOpen(true)}>
            Ingresar con PIN · {terminal.data.deviceName}
          </Button>
        )}
        {resetAvailable.data?.available ? (
          <Link to="/recuperar" className="bf-hint" style={{ textAlign: 'center' }}>
            ¿Olvidaste la contraseña?
          </Link>
        ) : (
          <p className="bf-hint" style={{ textAlign: 'center' }}>
            ¿Olvidaste la contraseña? Pedile al dueño de la carnicería que te genere una nueva desde Usuarios.
          </p>
        )}
      </form>
      <PinSwitchDialog
        open={pinOpen}
        onClose={() => setPinOpen(false)}
        onSwitched={() => navigate(safeNext(params.get('next')), { replace: true })}
      />
    </AuthLayout>
  );
}
